// NavTabs — shared nav-tab cluster for CommandBar's subscriber variants
// (CellsGrid, Benchmarking, Compare, Radar). DRY's the ~4x copy-pasted
// <div className="cmdbar-nav-tabs"> markup into one component.
//
// ION now leads the nav as the app's primary entry point (Ion as Insights) —
// it renders first, styled distinctly via `.cmdbar-nav-tab--ion`, with the
// existing Cell Explorer / Cell Selection / Technology Radar (+ Compare)
// group visually receded behind a divider as the secondary group. The ION
// tab self-guards on `ionHidden` (same signal AssistantRouteGuard uses) so
// it never appears as a dead link for users Ion is unavailable to.
//
// Callers still compute their own location-derived active flags
// (isCellsActive/isBenchmarkingActive/isRadarActive) — those conditions
// differ subtly per variant (e.g. Benchmarking/Compare don't treat
// `/explorer` as the Cell Explorer tab's active route the way CellsGrid/
// Radar do) — this component only renders markup from the flags it's given.
import { IonLockup } from '../chat/IonLockup.jsx'
import { ionHidden } from '../../state/chatStore.js'
import { radarNewBadgeSeen } from '../../state/radarState.js'

/**
 * @param {object} props
 * @param {string} props.location - current path, used only for the ION
 *   tab's active state (`/assistant`); other tabs' active flags are passed
 *   in explicitly since their conditions vary per variant.
 * @param {Function} props.setLocation
 * @param {string} [props.dataTour] - forwarded to the outer wrapper's
 *   `data-tour` attribute; only the CellsGrid variant sets this today.
 * @param {boolean} props.isCellsActive
 * @param {boolean} props.isBenchmarkingActive
 * @param {boolean} props.isRadarActive
 * @param {boolean} [props.showRadarBadge=true] - the Technology Radar "New"
 *   badge doesn't render on the Radar variant itself (you're already there).
 * @param {'auto'|'always'|false} [props.compareTab='auto'] - 'auto' shows
 *   the Compare tab only once something is in the cart (compareCount > 0 —
 *   Grid/Benchmarking behavior); 'always' always renders it (Compare
 *   variant); false omits it entirely (Radar variant — pre-existing
 *   behavior, independent of cart state).
 * @param {boolean} [props.isCompareActive=false]
 * @param {number} [props.compareCount=0]
 * @param {Function} [props.onCompareClick] - required when compareTab is
 *   truthy; differs per variant (Grid/Benchmarking persist
 *   `previousCellsView` before navigating, Compare does not).
 */
export default function NavTabs({
  location,
  setLocation,
  dataTour,
  isCellsActive,
  isBenchmarkingActive,
  isRadarActive,
  showRadarBadge = true,
  compareTab = 'auto',
  isCompareActive = false,
  compareCount = 0,
  onCompareClick,
}) {
  const isIonActive = location.startsWith('/assistant')

  return (
    <div className="cmdbar-nav-tabs" data-tour={dataTour}>
      {/* `ionHidden` is the only gate (2026-08-28). A second conjunct on the
          Ion-Home landing flag was labelled a temporary production hide but was
          inert in every deployed build — that flag is true in staging and
          production — so the tab has always rendered wherever `ionHidden`
          allowed it. Removed rather than left to mislead. */}
      {!ionHidden.value && (
        <button
          className={`cmdbar-nav-tab cmdbar-nav-tab--ion${isIonActive ? ' cmdbar-nav-tab--active' : ''}`}
          // Clicking the ION lockup is a request to START, not to resume — the
          // same act as clicking the logo in any assistant. It only NAVIGATES:
          // `/assistant` (no id) IS "a new chat", and AssistantPage is the single
          // place that rule lives. It used to also clear the store here, which
          // meant two places deciding the same thing — and the address bar could
          // disagree with what was on screen.
          onClick={() => setLocation('/assistant')}
        >
          <IonLockup size={15} />
          {/* "Preview" rather than "New": Ion ships as a sneak preview (the
              same framing as the SNEAK PREVIEW badge on its own header), so the
              word has to promise less than "New" does.

              Its own class, not the Radar's `--new`: that badge is solid orange
              on white, which on this tab sits on an already orange-tinted
              background and turns to mush. This one borrows the outline
              treatment of `.ion-header__preview-badge` instead. */}
          <span className="cmdbar-nav-badge cmdbar-nav-badge--preview">Preview</span>
        </button>
      )}

      <span className="cmdbar-nav-divider" aria-hidden="true" />

      <div className="cmdbar-nav-tabs__secondary">
        <button
          className={`cmdbar-nav-tab${isCellsActive ? ' cmdbar-nav-tab--active' : ''}`}
          onClick={() => setLocation('/cells')}
        >
          <span>Cell Explorer</span>
        </button>

        <button
          className={`cmdbar-nav-tab${isBenchmarkingActive ? ' cmdbar-nav-tab--active' : ''}`}
          onClick={() => setLocation('/cell-selection')}
        >
          Cell Selection
        </button>

        <button
          className={`cmdbar-nav-tab${isRadarActive ? ' cmdbar-nav-tab--active' : ''}`}
          onClick={() => setLocation('/radar')}
        >
          <span>Technology Radar</span>
          {/* The "New" badge moved to ION (2026-08-03, BA): the Radar is no
              longer the newest thing in the header, and two badges compete for
              the one glance the header gets. The `showRadarBadge` /
              `radarNewBadgeSeen` machinery is left in place — the seen-state is
              persisted per user and the badge is a prop away from returning for
              the next new surface. */}
        </button>

        {compareTab === 'always' && (
          <button
            className={`cmdbar-nav-tab${isCompareActive ? ' cmdbar-nav-tab--active' : ''}${compareCount > 0 ? ' has-badge' : ''}`}
            onClick={onCompareClick}
          >
            <span>Compare</span>
            {compareCount > 0 && <span className="cmdbar-nav-badge">{compareCount}</span>}
          </button>
        )}

        {compareTab === 'auto' && compareCount > 0 && (
          <button
            className={`cmdbar-nav-tab${isCompareActive ? ' cmdbar-nav-tab--active' : ''} has-badge`}
            onClick={onCompareClick}
          >
            <span>Compare</span>
            <span className="cmdbar-nav-badge">{compareCount}</span>
          </button>
        )}
      </div>
    </div>
  )
}
